import { AnnotationBrush, AnnotationPolygon, AnnotationRect } from './annotation.types';
import { computeBrushOutline } from './brush-outline.util';

export class ModalMouseHandlers {
  // TODO delete later
  private static toSafeJson<T>(value: T): T | string {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return '[unserializable]';
    }
  }

  private static logFullStructureSnapshot(ctx: any, trigger: string, pendingAnnotation?: any): void {
    const currentItem = Array.isArray(ctx.dataArray)
      ? ctx.dataArray[ctx.currentIndex]
      : undefined;

    const snapshot = {
      trigger,
      currentIndex: ctx.currentIndex,
      currentId: ctx.id,
      item: this.toSafeJson(currentItem),
      labelState: this.toSafeJson(currentItem?._labelState),
      annotationsInMemory: this.toSafeJson(ctx.annotations),
      pendingAnnotation: this.toSafeJson(pendingAnnotation),
      classes: {
        imageKnownClasses: this.toSafeJson(ctx.imageKnownClasses),
        annotationKnownClasses: this.toSafeJson(ctx.annotationKnownClasses),
        imageSessionClasses: this.toSafeJson(ctx.imageSessionClasses),
        annotationSessionClasses: this.toSafeJson(ctx.annotationSessionClasses),
        classColors: this.toSafeJson(ctx.classColors),
      },
      form: {
        model: this.toSafeJson(ctx.model),
        saveButtonDisabled: ctx.saveButtondisabled,
      },
    };

    console.log('[Annotation] FULL STRUCTURE SNAPSHOT', snapshot);
  }
  // TODO delete later

  static onMouseDown(ctx: any, event: MouseEvent, container: HTMLElement): void {
    if (ctx.activeTool) return;
    ctx.mouseDownTime = Date.now();
    ctx.isDragging = false;
    if (!ctx.isZoomed) return;
    // Snapshot drag origin and current pan offsets for pan calculations.
    ctx.startX = event.pageX - container.offsetLeft;
    ctx.startY = event.pageY - container.offsetTop;
    ctx.startPanX = ctx.panX ?? 0;
    ctx.startPanY = ctx.panY ?? 0;
    event.preventDefault();
  }

  static onMouseMove(ctx: any, event: MouseEvent, container: HTMLElement): void {
    if (ctx.activeTool) return;
    if (!ctx.isZoomed) return;
    const x = event.pageX - container.offsetLeft;
    const y = event.pageY - container.offsetTop;
    // Small threshold avoids switching to drag mode on tiny click jitter.
    const moved = Math.abs(x - ctx.startX) > 5 || Math.abs(y - ctx.startY) > 5;
    if (moved && event.buttons === 1) {
      ctx.isDragging = true;
      event.preventDefault();
      ctx.panX = (ctx.startPanX ?? 0) + (x - ctx.startX);
      ctx.panY = (ctx.startPanY ?? 0) + (y - ctx.startY);
      ctx.cdr.detectChanges();
      ctx.repositionCanvas();
    }
  }

  static onMouseUp(ctx: any): void {
    if (ctx.activeTool) return;
    const elapsed = Date.now() - ctx.mouseDownTime;
    if (elapsed < 200 && !ctx.isDragging) {
      ctx.toggleZoom();
    }
    ctx.isDragging = false;
  }

  static onImageContainerMouseDown(ctx: any, event: MouseEvent, container: HTMLElement): void {
    if (ctx.activeTool) {
      // With a tool selected, the same container event is treated as canvas input.
      this.onCanvasMouseDown(ctx, event);
      return;
    }
    this.onMouseDown(ctx, event, container);
  }

  static onImageContainerMouseMove(ctx: any, event: MouseEvent, container: HTMLElement): void {
    if (ctx.activeTool) {
      this.onCanvasMouseMove(ctx, event);
      return;
    }
    this.onMouseMove(ctx, event, container);
  }

  static onImageContainerMouseUp(ctx: any, event: MouseEvent): void {
    if (ctx.activeTool) {
      this.onCanvasMouseUp(ctx, event);
      return;
    }
    this.onMouseUp(ctx);
  }

  static onWindowMouseMove(ctx: any, event: MouseEvent): void {
    if (!ctx.activeTool) return;
    if (this.isMouseInsideImageContainer(ctx, event)) return;
    // Continue active draw/edit interactions even when pointer leaves the container.
    this.onCanvasMouseMove(ctx, event);
  }

  static onWindowMouseUp(ctx: any, event: MouseEvent): void {
    if (!ctx.activeTool) return;
    if (this.isMouseInsideImageContainer(ctx, event)) return;
    this.onCanvasMouseUp(ctx, event);
  }

  static onCanvasMouseDown(ctx: any, event: MouseEvent): void {
    event.stopPropagation();
    if (!ctx.activeTool) return;
    const { x, y } = ctx.getCanvasImageCoords(event);

    if (ctx.activeTool === 'brush') {
      // Brush stores center-line points; shape is converted to outline on mouseup.
      ctx.inProgressBrush = [{ x, y }];
      return;
    }
    if (ctx.activeTool === 'rect') {
      ctx.inProgressRect = { startX: x, startY: y, endX: x, endY: y };
    } else if (ctx.activeTool === 'polygon') {
      if (!ctx.inProgressPolygon) {
        ctx.inProgressPolygon = [{ x, y }];
      } else {
        ctx.inProgressPolygon.push({ x, y });
      }
      ctx.redrawCanvas();
    } else if (ctx.activeTool === 'edit-points') {
      const hitR = Math.max(10, 10 / ctx.zoomLevel);
      // Pick top-most point first by scanning annotations in reverse draw order.
      for (let ai = ctx.annotations.length - 1; ai >= 0; ai--) {
        const pts = ctx.getAnnotationCorners(ctx.annotations[ai]);
        for (let pi = 0; pi < pts.length; pi++) {
          if (Math.hypot(x - pts[pi].x, y - pts[pi].y) <= hitR) {
            ctx.editPointAnnIdx = ai;
            ctx.editPointIdx = pi;
            if (ctx.annotations[ai].type === 'rect') {
              const oppositeIdx = (pi + 2) % 4;
              ctx.editPointAnchor = { x: pts[oppositeIdx].x, y: pts[oppositeIdx].y };
            }
            return;
          }
        }
      }
    } else if (ctx.activeTool === 'move-shape') {
      for (let ai = ctx.annotations.length - 1; ai >= 0; ai--) {
        if (ctx.isPointInAnnotation(x, y, ctx.annotations[ai])) {
          ctx.moveAnnIdx = ai;
          ctx.moveStartX = x;
          ctx.moveStartY = y;
          ctx.moveSnapshot = JSON.parse(JSON.stringify(ctx.annotations[ai]));
          return;
        }
      }
    }
  }

  static onCanvasMouseMove(ctx: any, event: MouseEvent): void {
    if (!ctx.activeTool) return;
    const { x, y } = ctx.getCanvasImageCoords(event);

    if (ctx.activeTool === 'brush') {
      if (ctx.inProgressBrush && event.buttons === 1) {
        const last = ctx.inProgressBrush[ctx.inProgressBrush.length - 1];
        const dist = Math.hypot(x - last.x, y - last.y);
        // Point spacing throttles path growth and keeps brush rendering smoother.
        if (dist >= 2) {
          ctx.inProgressBrush.push({ x, y });
          ctx.redrawCanvas();
        }
      } else if (event.buttons === 0) {
        ctx.brushCursorPos = { x, y };
        ctx.redrawCanvas();
      }
      return;
    }
    if (ctx.activeTool === 'rect') {
      if (ctx.inProgressRect && event.buttons === 1) {
        ctx.inProgressRect.endX = x;
        ctx.inProgressRect.endY = y;
        ctx.rectCursorPos = null;
      } else if (!ctx.inProgressRect) {
        ctx.rectCursorPos = { x, y };
      }
      ctx.redrawCanvas();
    } else if (ctx.activeTool === 'polygon' && ctx.inProgressPolygon) {
      ctx.polygonMousePos = { x, y };
      ctx.redrawCanvas();
    } else if (ctx.activeTool === 'edit-points') {
      if (event.buttons === 0) {
        const hitR = Math.max(10, 10 / ctx.zoomLevel);
        let foundAnn = -1;
        let foundPt = -1;
        outer: for (let ai = ctx.annotations.length - 1; ai >= 0; ai--) {
          const pts = ctx.getAnnotationCorners(ctx.annotations[ai]);
          for (let pi = 0; pi < pts.length; pi++) {
            if (Math.hypot(x - pts[pi].x, y - pts[pi].y) <= hitR) {
              foundAnn = ai;
              foundPt = pi;
              break outer;
            }
          }
        }
        if (ctx.hoverAnnIdx !== foundAnn || ctx.hoverPointIdx !== foundPt) {
          // Redraw only when hover target changes to reduce unnecessary canvas work.
          ctx.hoverAnnIdx = foundAnn;
          ctx.hoverPointIdx = foundPt;
          ctx.redrawCanvas();
        }
      }
      if (ctx.editPointIdx !== -1 && ctx.editPointAnnIdx !== -1 && event.buttons === 1) {
        const ann = ctx.annotations[ctx.editPointAnnIdx];
        if (ann.type === 'polygon' || ann.type === 'brush') {
          ann.points[ctx.editPointIdx] = { x, y };
        } else if (ann.type === 'rect' && ctx.editPointAnchor) {
          const a = ctx.editPointAnchor;
          ann.x = Math.min(x, a.x);
          ann.y = Math.min(y, a.y);
          ann.width = Math.abs(x - a.x);
          ann.height = Math.abs(y - a.y);
        }
        ctx.redrawCanvas();
      }
    } else if (ctx.activeTool === 'move-shape') {
      if (event.buttons === 0) {
        let found = -1;
        for (let ai = ctx.annotations.length - 1; ai >= 0; ai--) {
          if (ctx.isPointInAnnotation(x, y, ctx.annotations[ai])) {
            found = ai;
            break;
          }
        }
        if (ctx.hoverAnnIdx !== found) {
          ctx.hoverAnnIdx = found;
          ctx.redrawCanvas();
        }
      }
      if (ctx.moveAnnIdx !== -1 && ctx.moveSnapshot && event.buttons === 1) {
        const dx = x - ctx.moveStartX;
        const dy = y - ctx.moveStartY;
        const ann = ctx.annotations[ctx.moveAnnIdx];
        const snap = ctx.moveSnapshot;
        const canvas = ctx.annotationCanvasRef?.nativeElement;
        if (ann.type === 'rect' && snap.type === 'rect') {
          // Keep moved rectangles fully inside canvas bounds.
          const maxX = Math.max(0, (canvas?.width ?? 0) - snap.width);
          const maxY = Math.max(0, (canvas?.height ?? 0) - snap.height);
          ann.x = Math.min(Math.max(snap.x + dx, 0), maxX);
          ann.y = Math.min(Math.max(snap.y + dy, 0), maxY);
        } else if ((ann.type === 'polygon' || ann.type === 'brush') && (snap.type === 'polygon' || snap.type === 'brush')) {
          const points = snap.points;
          let minX = Infinity;
          let maxX = -Infinity;
          let minY = Infinity;
          let maxY = -Infinity;
          for (const p of points) {
            minX = Math.min(minX, p.x);
            maxX = Math.max(maxX, p.x);
            minY = Math.min(minY, p.y);
            maxY = Math.max(maxY, p.y);
          }
          // Clamp translation using the source-shape bounds so all points stay visible.
          const minDx = -minX;
          const maxDx = (canvas?.width ?? 0) - maxX;
          const minDy = -minY;
          const maxDy = (canvas?.height ?? 0) - maxY;
          const clampedDx = Math.min(Math.max(dx, minDx), maxDx);
          const clampedDy = Math.min(Math.max(dy, minDy), maxDy);
          ann.points = points.map((p: any) => ({ x: p.x + clampedDx, y: p.y + clampedDy }));
        }
        ctx.redrawCanvas();
      }
    }
  }

  static onCanvasMouseUp(ctx: any, event: MouseEvent): void {
    event.stopPropagation();
    if (ctx.activeTool === 'brush') {
      if (ctx.inProgressBrush && ctx.inProgressBrush.length >= 1) {
        const halfWidth = ctx.getBrushHalfWidth();
        // Convert sampled stroke into a closed polygon before labeling.
        const outlinePoints = computeBrushOutline(ctx.inProgressBrush, halfWidth);
        const annotation: AnnotationBrush = { type: 'brush', points: outlinePoints };
        ctx.openLabelPopup(annotation);
      }
      ctx.inProgressBrush = null;
      ctx.redrawCanvas();
      return;
    }
    if (ctx.activeTool === 'rect' && ctx.inProgressRect) {
      const { startX, startY, endX, endY } = ctx.inProgressRect;
      const x = Math.min(startX, endX);
      const y = Math.min(startY, endY);
      const width = Math.abs(endX - startX);
      const height = Math.abs(endY - startY);
      if (width > 2 && height > 2) {
        const annotation: AnnotationRect = { type: 'rect', x, y, width, height };
        console.log('[Annotation] Rect erstellt (vor Label):', annotation);
        ctx.openLabelPopup(annotation);
      }
      ctx.inProgressRect = null;
      ctx.redrawCanvas();
    } else if (ctx.activeTool === 'edit-points' && ctx.editPointIdx !== -1) {
      const ann = ctx.annotations[ctx.editPointAnnIdx];
      console.log('[Annotation] Punkt verschoben:', { ...ann });
      ctx.editPointIdx = -1;
      ctx.editPointAnnIdx = -1;
      ctx.editPointAnchor = null;
      ctx.saveAnnotationsToCurrentItem();
      ctx.redrawCanvas();
    } else if (ctx.activeTool === 'move-shape' && ctx.moveAnnIdx !== -1) {
      const ann = ctx.annotations[ctx.moveAnnIdx];
      //TODO delete later
      console.log('[Annotation] Form verschoben:', { ...ann });
      //TODO delete later
      ctx.moveAnnIdx = -1;
      ctx.moveSnapshot = null;
      ctx.saveAnnotationsToCurrentItem();
    }
  }

  static onCanvasDblClick(ctx: any, event: MouseEvent): void {
    event.stopPropagation();
    if (ctx.activeTool === 'polygon' && ctx.inProgressPolygon) {
      // dblclick fires two mousedowns first -> pop the duplicate last point
      ctx.inProgressPolygon.pop();
      if (ctx.inProgressPolygon.length >= 3) {
        // Only close polygons that are valid enough to render/edit later.
        const annotation: AnnotationPolygon = { type: 'polygon', points: [...ctx.inProgressPolygon] };
        this.logFullStructureSnapshot(ctx, 'polygon-finished-before-label', annotation);
        ctx.openLabelPopup(annotation);
      }
      ctx.inProgressPolygon = null;
      ctx.polygonMousePos = null;
      ctx.redrawCanvas();
    }
  }

  static onEnterFinishPolygon(ctx: any): void {
    if (ctx.activeTool === 'polygon' && ctx.inProgressPolygon && !ctx.pendingAnnotation) {
      // Enter key: no preceding extra mousedown, so no point needs to be popped.
      if (ctx.inProgressPolygon.length >= 3) {
        const annotation: AnnotationPolygon = { type: 'polygon', points: [...ctx.inProgressPolygon] };
        this.logFullStructureSnapshot(ctx, 'polygon-finished-before-label-enter', annotation);
        ctx.openLabelPopup(annotation);
      }
      ctx.inProgressPolygon = null;
      ctx.polygonMousePos = null;
      ctx.redrawCanvas();
    }
  }

  static onCanvasMouseLeave(ctx: any): void {
    ctx.polygonMousePos = null;
    ctx.brushCursorPos = null;
    ctx.rectCursorPos = null;
    ctx.hoverAnnIdx = -1;
    ctx.hoverPointIdx = -1;
    ctx.redrawCanvas();
  }

  private static isMouseInsideImageContainer(ctx: any, event: MouseEvent): boolean {
    const container = ctx.imageContainerRef?.nativeElement;
    if (!container) return false;
    const rect = container.getBoundingClientRect();
    return event.clientX >= rect.left &&
      event.clientX <= rect.right &&
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom;
  }
}
